﻿import { toCyrillic, toLatin } from "serbian-transliteration";

declare global {
    interface Window {
        transliterate: (toCyrillicFlag?: boolean) => void;
        gtag?: (command: string, action: string, parameters?: any) => void;
        google?: {
            translate: {
                TranslateElement: new (elementId: string) => any;
            };
        };
        getElementHeight: (element: Element) => number | null;
        getElementDistanceToTop: (element: Element) => number | null;
        getElementDistanceToBottom: (element: Element) => number | null;
    }
}

const STORAGE_KEY = 'cyrillicMode';

function getCyrillicModeFromStorage(): boolean {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored !== null ? JSON.parse(stored) : true; // Default to true if not set
}

function setCyrillicModeToStorage(value: boolean): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

let isCyrillicMode: boolean = getCyrillicModeFromStorage();

function normalizeDisplayedWebUrl(value: string): string | null {
    let candidate = value.trim().replace(/\s+/g, '');
    if (!candidate) return null;

    if (candidate.startsWith('//')) {
        candidate = `https:${candidate}`;
    } else if (!/^[a-z][a-z\d+.-]*:\/\//i.test(candidate)) {
        if (!/^(?:www\.)?[^/?#]+\.[^/?#]+(?:[/?#]|$)/i.test(candidate)) return null;
        candidate = `https://${candidate}`;
    }

    try {
        const url = new URL(candidate);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

        const hostname = url.hostname.toLocaleLowerCase().replace(/^www\./i, '');
        const pathname = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
        return `${hostname}${url.port ? `:${url.port}` : ''}${pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}

function displaysItsDestination(anchor: HTMLAnchorElement): boolean {
    const text = anchor.textContent?.trim().replace(/\s+/g, '') ?? '';
    const href = anchor.getAttribute('href')?.trim() ?? '';
    if (!text || !href) return false;

    if (text === href) return true;

    if (/^mailto:/i.test(href)) {
        return text === href.substring(href.indexOf(':') + 1);
    }

    if (/^tel:/i.test(href)) {
        const normalizePhoneNumber = (value: string): string => value.replace(/[^\d+]/g, '');
        return normalizePhoneNumber(text) === normalizePhoneNumber(href.substring(href.indexOf(':') + 1));
    }

    const displayedUrl = normalizeDisplayedWebUrl(text);
    const destinationUrl = normalizeDisplayedWebUrl(href);
    return displayedUrl !== null && displayedUrl === destinationUrl;
}

function shouldTransliterateTextNode(node: Node): boolean {
    const parent = node.parentElement;
    if (!parent || parent.closest('[data-no-transliterate], #cconsent-modal, #cconsent-bar')) return false;

    const anchor = parent.closest('a');
    return !(anchor instanceof HTMLAnchorElement && displaysItsDestination(anchor));
}

export function transliterate(): void {
    const toCyrillicFlag: boolean = getCyrillicModeFromStorage();
    const elements: HTMLElement[] = [
        document.getElementsByClassName("main")[0],
        document.getElementsByClassName("ccb__wrapper")[0],
        document.getElementsByClassName("ccm__content")[0]
    ].filter((el): el is HTMLElement => el instanceof HTMLElement);

    elements.forEach((element: HTMLElement) => {
        const walker: TreeWalker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null);

        let node: Node | null;
        while ((node = walker.nextNode())) {
            if (node.nodeType === Node.TEXT_NODE && node.nodeValue && shouldTransliterateTextNode(node)) {
                const text: string = node.nodeValue.trim();
                if (!text) continue;

                node.nodeValue = toCyrillicFlag
                    ? toCyrillic(node.nodeValue)
                    : toLatin(node.nodeValue);
            }
        }
    });
}

function toggleTransliteration(): void {
    isCyrillicMode = !isCyrillicMode;
    setCyrillicModeToStorage(isCyrillicMode); // Save to localStorage
    transliterate();
    updateToggleButton();
}

function updateToggleButton(): void {
    const toggleButton = document.getElementById('transliterate-toggle') as HTMLButtonElement;
    const toggleText = toggleButton?.querySelector('.transliterate-text') as HTMLSpanElement;

    if (toggleButton && toggleText) {
        if (isCyrillicMode) {
            toggleText.textContent = 'Latinica';
            toggleButton.setAttribute('aria-pressed', 'true');
            toggleButton.classList.add('active');
        } else {
            toggleText.textContent = 'Ћирилица';
            toggleButton.setAttribute('aria-pressed', 'false');
            toggleButton.classList.remove('active');
        }
    }
}

function initializeTransliterationToggle(): void {
    const toggleButton = document.getElementById('transliterate-toggle') as HTMLButtonElement;

    if (toggleButton) {
        toggleButton.setAttribute('aria-pressed', 'false');
        toggleButton.setAttribute('aria-label', 'Toggle between Latin and Cyrillic script');

        toggleButton.addEventListener('click', toggleTransliteration);

        updateToggleButton();
    }
}

window.transliterate = transliterate;

function onTranslateEvent(lang: string): void {
    if (window.gtag) {
        window.gtag('event', 'translate', {
            event_category: 'Localization',
            event_label: lang // e.g. "fr", "de"
        });
    }
}

function googleTranslateElementInit(): void {
    if (window.google?.translate?.TranslateElement) {
        new window.google.translate.TranslateElement('google_translate_element');
        const select = document.querySelector('#google_translate_element select') as HTMLSelectElement;
        select?.addEventListener('change', (e: Event) => {
            const target = e.target as HTMLSelectElement;
            onTranslateEvent(target.value);
        });
    }
}

interface FaqItem {
    question: string;
    answer: string;
    link?: { url: string; label: string };
}

function initializeAccordion(accordion: Element): void {
    const toggleButton = accordion.querySelector('.accordion-toggle') as HTMLButtonElement;
    const content = accordion.querySelector('.accordion-content') as HTMLDivElement;
    const icon = accordion.querySelector('.accordion-icon') as SVGElement;

    if (!toggleButton || !content || !icon) return;

    toggleButton.addEventListener('click', (): void => {
        const isOpen = toggleButton.getAttribute('aria-expanded') !== 'true';
        toggleButton.setAttribute('aria-expanded', isOpen.toString());
        content.style.maxHeight = isOpen ? `${content.scrollHeight}px` : '0';
        icon.classList.toggle('rotate', isOpen);
    });
}

async function initializeFaqs(): Promise<void> {
    const container = document.querySelector<HTMLElement>('[data-faqs]');
    const data = document.getElementById('faq-data');
    if (!container || !data?.textContent) return;

    try {
        const faqs = JSON.parse(data.textContent) as FaqItem[];
        const fragment = document.createDocumentFragment();

        faqs.forEach((faq, index) => {
            const accordion = document.createElement('article');
            accordion.className = 'accordion faq-accordion';

            const button = document.createElement('button');
            button.className = 'accordion-toggle';
            button.type = 'button';
            button.setAttribute('aria-expanded', 'false');
            button.setAttribute('aria-controls', `faq-answer-${index}`);
            button.innerHTML = `<span class="accordion-heading"><span class="accordion-title"></span></span><svg class="accordion-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>`;
            button.querySelector('.accordion-title')!.textContent = faq.question;

            const content = document.createElement('div');
            content.className = 'accordion-content';
            content.id = `faq-answer-${index}`;
            const answer = document.createElement('div');
            const paragraph = document.createElement('p');
            paragraph.textContent = faq.answer;
            answer.appendChild(paragraph);

            if (faq.link) {
                const link = document.createElement('a');
                link.href = faq.link.url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.textContent = faq.link.label;
                answer.appendChild(link);
            }

            content.appendChild(answer);
            accordion.append(button, content);
            initializeAccordion(accordion);
            fragment.appendChild(accordion);
        });

        container.replaceChildren(fragment);
    } catch {
        container.textContent = 'Često postavljana pitanja trenutno nisu dostupna. Pokušajte ponovo kasnije.';
        container.classList.add('faq-accordions__error');
    }
}

document.addEventListener('DOMContentLoaded', (): void => {
    initializeTransliterationToggle();
    document.querySelectorAll('.accordion').forEach(initializeAccordion);
    void initializeFaqs();
});

window.getElementHeight = (element: Element): number | null => {
    if (!element) {
        return null;
    }

    const htmlElement = element as HTMLElement;
    const offsetHeight: number = htmlElement.offsetHeight;

    if (!offsetHeight || offsetHeight > (window.innerHeight - element.getBoundingClientRect().top)) {
        return Math.floor(window.innerHeight - element.getBoundingClientRect().top);
    }

    return Math.floor(offsetHeight);
};

window.getElementDistanceToTop = (element: Element): number | null => {
    if (!element) {
        return null;
    }

    return Math.floor(element.getBoundingClientRect().y);
};

window.getElementDistanceToBottom = (element: Element): number | null => {
    if (!element) {
        return null;
    }

    return Math.floor(window.innerHeight - element.getBoundingClientRect().y);
};
